<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { searchApi } from '../api'
import type { ConfigSearchHit } from '../types'

const props = defineProps<{ visible: boolean }>()
const emit  = defineEmits<{ (e: 'update:visible', v: boolean): void }>()

const router  = useRouter()
const query   = ref('')
const results = ref<ConfigSearchHit[]>([])
const loading = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)

let debounceTimer: ReturnType<typeof setTimeout> | null = null

watch(() => props.visible, (v) => {
  if (v) {
    query.value = ''
    results.value = []
    nextTick(() => inputRef.value?.focus())
  }
})

watch(query, (q) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  if (!q.trim()) { results.value = []; return }
  debounceTimer = setTimeout(() => runSearch(q.trim()), 300)
})

async function runSearch(q: string) {
  loading.value = true
  try {
    const r = await searchApi.search(q)
    results.value = r.data.results ?? []
  } catch {
    results.value = []
  } finally {
    loading.value = false
  }
}

function close() {
  emit('update:visible', false)
}

function openResult(hit: ConfigSearchHit) {
  close()
  // Detect if this is a site file (under sites-available) or a global config file
  const isSite = hit.file.includes('sites-available') || hit.file.includes('sites-enabled')
  if (isSite) {
    const base = hit.file.split('/').pop() ?? ''
    router.push(`/sites/${encodeURIComponent(base)}`)
  } else {
    router.push({ path: '/config', query: { path: hit.file, line: String(hit.line_num) } })
  }
}

// Group results by file
import { computed } from 'vue'
const grouped = computed(() => {
  const map = new Map<string, ConfigSearchHit[]>()
  for (const h of results.value) {
    if (!map.has(h.file)) map.set(h.file, [])
    map.get(h.file)!.push(h)
  }
  return map
})
</script>

<template>
  <el-dialog
    :model-value="visible"
    @update:model-value="emit('update:visible', $event)"
    title=""
    width="620px"
    :show-close="false"
    class="search-dialog"
    @keydown.esc="close"
  >
    <div class="search-box">
      <el-input
        ref="inputRef"
        v-model="query"
        placeholder="Search config files… (Ctrl+K)"
        size="large"
        clearable
        :prefix-icon="loading ? undefined : undefined"
      >
        <template #prefix>
          <span style="color:#9ca3af">⌕</span>
        </template>
        <template #suffix>
          <el-tag v-if="loading" size="small" type="info">searching…</el-tag>
          <el-tag v-else-if="results.length" size="small" type="success">{{ results.length }} hits</el-tag>
          <kbd class="esc-hint">Esc</kbd>
        </template>
      </el-input>
    </div>

    <div v-if="!query" class="empty-hint">
      Type to search across all nginx config files
    </div>
    <div v-else-if="!loading && results.length === 0" class="empty-hint">
      No matches found
    </div>

    <div v-else class="results-list">
      <template v-for="[file, hits] in grouped" :key="file">
        <div class="file-header">{{ file }}</div>
        <div
          v-for="hit in hits"
          :key="hit.line_num"
          class="result-row"
          @click="openResult(hit)"
        >
          <span class="line-num">{{ hit.line_num }}</span>
          <span class="line-content">{{ hit.line }}</span>
        </div>
      </template>
    </div>
  </el-dialog>
</template>

<style scoped>
.search-box { margin-bottom: 12px; }
.empty-hint { color: #9ca3af; text-align: center; padding: 24px 0; font-size: 14px; }
.results-list { max-height: 400px; overflow-y: auto; }
.file-header {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: .05em;
  padding: 8px 4px 4px;
  border-top: 1px solid #f3f4f6;
}
.file-header:first-child { border-top: none; }
.result-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 5px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-family: monospace;
  font-size: 13px;
}
.result-row:hover { background: #f3f4f6; }
.line-num { color: #9ca3af; min-width: 36px; text-align: right; flex-shrink: 0; }
.line-content { color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.esc-hint {
  font-size: 10px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  padding: 1px 5px;
  color: #6b7280;
  cursor: pointer;
}
</style>
